import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ApiClientService, PermissionService, RightPanelService } from '@services/index';
import { TablePreferencesService } from '@services/local-storage/table-preferences.service';
import { Align, Permission } from 'app/enums';
import { DeviceStatus } from 'app/enums/device-status.enum';
import { DeviceCalibrationStatus } from 'app/enums/device-calibration-status.enum';
import { Device, SortType } from 'app/types';
import { Filter, FilterForm } from './device-list.model';
import { BehaviorSubject, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { GetDevicesResponse } from '@services/api-client/responses';
import { sortStrings } from '@helpers/sort-string-helper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CreateAndUpdateDeviceModalComponent } from '../../components/create-and-update-device-modal/create-and-update-device-modal.component';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
    selector: 'app-device-list',
    templateUrl: './device-list.component.html',
    styleUrl: './device-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DeviceListComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly rightPanelService = inject(RightPanelService);
    private readonly translateService = inject(TranslateService);
    private readonly permissionService = inject(PermissionService);
    private readonly tablePreferencesService = inject(TablePreferencesService);

    private static readonly tableId = 'device-list' as const;

    public readonly align = Align;
    public readonly permissions = Permission;
    public sortKey: keyof Device = 'serialNumber';
    public sortAscending = true;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public loadingDeviceIds: string[] = [];
    private readonly updateSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public update$: Observable<void> = this.updateSubject.asObservable();
    private readonly searchSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public search$: Observable<void> = this.searchSubject.asObservable();
    private readonly sortSubject: BehaviorSubject<SortType<Device>> = new BehaviorSubject({
        key: this.sortKey,
        ascending: this.sortAscending
    });
    public sort$: Observable<SortType<Device>> = this.sortSubject.asObservable();
    public devices$: Observable<GetDevicesResponse> = new Observable<GetDevicesResponse>();
    private readonly deletedDeviceIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public deletedDeviceIds$: Observable<string[]> = this.deletedDeviceIdsSubject.asObservable();
    public readonly isUnrestricted = this.permissionService.isUnrestricted();

    public get f() {
        return this.filterForm.controls;
    }

    public ngOnInit() {
        const saved = this.tablePreferencesService.getPreferences(DeviceListComponent.tableId)?.sort;
        if (saved) {
            this.sortKey = saved.key as keyof Device;
            this.sortAscending = saved.ascending;
            this.sortSubject.next({ key: this.sortKey, ascending: this.sortAscending });
        }

        const fb = new FormBuilder();
        this.filterForm = fb.nonNullable.group(this.defaultFilter);
        this.devices$ = this.update$.pipe(
            tap(() => {
                this.deletedDeviceIdsSubject.next([]);
            }),
            switchMap(() =>
                this.apiClientService.getDevices$().pipe(
                    switchMap((device: GetDevicesResponse) =>
                        this.deletedDeviceIds$.pipe(
                            map((deletedDeviceIds) => device.filter((d) => !deletedDeviceIds.includes(d.id)))
                        )
                    ),
                    switchMap((devices: GetDevicesResponse) =>
                        this.search$.pipe(
                            map(() => {
                                return this.filterResponse(devices);
                            })
                        )
                    ),
                    switchMap((devices: GetDevicesResponse) =>
                        this.sort$.pipe(
                            switchMap((sort) => {
                                this.sortKey = sort.key;
                                this.sortAscending = sort.ascending;
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                return of(sortStrings<Device>(devices, sort.key, sort.ascending));
                            })
                        )
                    )
                )
            )
        );

        this.filterForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.searchSubject.next();
        });
    }

    private filterResponse(devices: Device[]): Device[] {
        const searchPhrase = this.f.searchPhrase.value.toLowerCase().trim();

        return devices.filter((device) => {
            return device.serialNumber.toLowerCase().includes(searchPhrase);
        });
    }

    public openCreateDeviceModal(device?: Device) {
        const title = this.translateService.instant('MENU.DEVICE') as string;
        this.rightPanelService.showComponent(
            title,
            CreateAndUpdateDeviceModalComponent,
            { editDevice: device },
            true,
            false,
            false,
            'pacemaker'
        );
        this.rightPanelService.isVisible$
            .pipe(
                filter((visible: boolean) => !visible),
                take(1),
                untilDestroyed(this)
            )
            .subscribe(() => {
                this.updateSubject.next();
            });
    }

    public onSortChange(key: keyof Device, ascending: boolean) {
        this.tablePreferencesService.setPreferences(DeviceListComponent.tableId, {
            sort: { key: key as string, ascending }
        });
        this.sortSubject.next({ key, ascending });
    }

    public getStatusColorClass(statusId: DeviceStatus): string {
        switch (statusId) {
            case DeviceStatus.NotVerified:
                return 'status-not-verified';
            case DeviceStatus.Verified:
                return 'status-verified';
            case DeviceStatus.Invalid:
                return 'status-invalid';
            case DeviceStatus.Failed:
                return 'status-failed';
            case DeviceStatus.Unknown:
            default:
                return '';
        }
    }

    public getStatusTooltipKey(statusId: DeviceStatus): string {
        switch (statusId) {
            case DeviceStatus.NotVerified:
                return 'FEATURES.DEVICE.STATUS_TOOLTIPS.NOT_VERIFIED';
            case DeviceStatus.Verified:
                return 'FEATURES.DEVICE.STATUS_TOOLTIPS.VERIFIED';
            case DeviceStatus.Invalid:
                return 'FEATURES.DEVICE.STATUS_TOOLTIPS.INVALID';
            case DeviceStatus.Failed:
                return 'FEATURES.DEVICE.STATUS_TOOLTIPS.FAILED';
            case DeviceStatus.Unknown:
            default:
                return '';
        }
    }

    public getCalibrationTooltip(item: Device): string {
        return this.translateService.instant('FEATURES.DEVICE.CALIBRATION_STATUS_TOOLTIP', {
            ongoing: this.getCalibrationStatusLabel(item.ongoingCalibrationStatusId)
        }) as string;
    }

    private getCalibrationStatusLabel(statusId: DeviceCalibrationStatus | null | undefined): string {
        if (statusId === null || statusId === undefined) {
            return '-';
        }

        return this.translateService.instant('FEATURES.DEVICE.CALIBRATION_STATUSES.' + statusId) as string;
    }

    public getStatusLabel(statusId: DeviceStatus): string {
        switch (statusId) {
            case DeviceStatus.NotVerified:
                return 'Not Verified';
            case DeviceStatus.Verified:
                return 'Verified';
            case DeviceStatus.Invalid:
                return 'Invalid';
            case DeviceStatus.Failed:
                return 'Failed';
            case DeviceStatus.Unknown:
            default:
                return '';
        }
    }
}
